import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { timestampFormat } from 'constants/businessAccounts';
import { codes as methodCodes } from 'constants/payment';

import BusinessAccountQuery from 'components/queries/BusinessAccountQuery';
import AlertBox from 'components/ui/AlertBox';
import { CancelButton } from 'components/ui/Button';
import AccountDetails from './AccountDetails';
import AccountForm from './AccountForm';

import { handlePaymentSubmit } from '../../SplitPayment/SplitPayment.utils';
import SplitPayment from '../../SplitPayment';

const BusinessAccountPayment = ({
  totalPriceToBePaid,
  currentAmount,
  onAmountChange,
  currency,
  loading,
  isOnHold,
  reference,
  addPayment,
  takeDeposit,
  payAndConfirm,
  isPutOnHoldWithDeposit
}) => {
  const [isAccountNotFound, setAccountNotFound] = useState(false);

  if (isAccountNotFound) {
    return (
      <>
        <AlertBox variant={AlertBox.variants.ERROR}>
          <FormattedMessage id="baf.accountNotFound" />
        </AlertBox>
        <CancelButton onClick={() => setAccountNotFound(false)}>
          <FormattedMessage id="baf.new" />
        </CancelButton>
      </>
    );
  }

  return (
    <BusinessAccountQuery
      onAccountNotFound={() => setAccountNotFound(true)}
      onError={(err) => {
        throw new AgentPortalError(err, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
          errorCTARedirect: '/'
        });
      }}
    >
      {(performQuery, { loading: queryLoading, data, resetQuery }) => {
        const onSubmit = async (e) => {
          e.preventDefault();
          await handlePaymentSubmit({
            bookingReference: reference,
            cashTendered: currentAmount,
            totalPriceToBePaid,
            payAndConfirm,
            takeDeposit,
            addPayment,
            isOnHold,
            currency,
            method: methodCodes.BUSINESS_ACCOUNT,
            externalReference: data.name,
            reference: `${data.accountNumber}-${moment().format(timestampFormat)}`
          });
          resetQuery();
        };

        const parsedAmount = Number(currentAmount);

        return (
          <SplitPayment
            title={<FormattedMessage id="payments.AmountToPayByBusinessAccount" />}
            subTitle={<FormattedMessage id="payments.canBeSplit" />}
            label={<FormattedMessage id="payments.recordPayment" />}
            loading={loading}
            onSubmit={onSubmit}
            hideSubmit={data === null || !data.active}
          >
            <>
              {data && !data.active && (
                <AlertBox variant={AlertBox.variants.WARNING}>
                  <FormattedMessage id="baf.accountNotActive" />
                </AlertBox>
              )}
              {data ? (
                <AccountDetails currency={currency} account={data} resetQuery={resetQuery} paymentAmount={parsedAmount} />
              ) : (
                <AccountForm
                  isSplitPayment
                  disablePayment={isPutOnHoldWithDeposit ? false : isOnHold}
                  currency={currency}
                  totalPriceToBePaid={totalPriceToBePaid}
                  performQuery={performQuery}
                  amount={parsedAmount}
                  onUpdateAmount={onAmountChange}
                  loading={queryLoading}
                  paymentUrlPrefix="/"
                />
              )}
            </>
          </SplitPayment>
        );
      }}
    </BusinessAccountQuery>
  );
};

BusinessAccountPayment.propTypes = {
  totalPriceToBePaid: PropTypes.number,
  currentAmount: PropTypes.string,
  onAmountChange: PropTypes.func,
  currency: PropTypes.string,
  isOnHold: PropTypes.bool,
  loading: PropTypes.bool,
  reference: PropTypes.string,
  addPayment: PropTypes.func,
  takeDeposit: PropTypes.func,
  payAndConfirm: PropTypes.func,
  isPutOnHoldWithDeposit: PropTypes.bool
};

export default BusinessAccountPayment;
